import React, { createContext, useState, useEffect, useContext } from 'react';
import { Product, CartItem } from '../types';
import { applyAutomaticPromotions, AutomaticPromotionDefinition } from '../utils/automaticPromotions';
import { getProductStockLimit, isProductOutOfStock } from '../utils/productAvailability';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ... existing code ...
interface CartContextType {
    cart: CartItem[];
    addToCart: (product: Product, quantity?: number) => void;
    removeFromCart: (itemKey: number | string) => void;
    decreaseQuantity: (itemKey: number | string) => void;
    clearCart: () => void;
    totalItems: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const createCartKey = (product: Product) => {
    const selections = product.promo_selecciones || [];
    if (!selections.length) return String(product.idProductos);

    const fingerprint = selections
        .map(selection => `${selection.id_grupo || 0}:${selection.id_producto}:${selection.cantidad}`)
        .sort()
        .join('|');

    return `${product.idProductos}:${fingerprint}`;
};

const matchesItemKey = (item: CartItem, key: number | string) => {
    const normalized = String(key);
    return item.cartKey === normalized || String(item.idProductos) === normalized;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [automaticPromotions, setAutomaticPromotions] = useState<AutomaticPromotionDefinition[]>([]);
    const [cart, setCart] = useState<CartItem[]>(() => {
        const savedCart = localStorage.getItem('cart');
        if (!savedCart) return [];
        try {
            const parsed = JSON.parse(savedCart) as CartItem[];
            return parsed.map(item => ({ ...item, cartKey: item.cartKey || createCartKey(item) }));
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        const controller = new AbortController();
        fetch(`${API_URL}/promociones-automaticas`, { signal: controller.signal })
            .then(response => response.ok ? response.json() : Promise.reject(new Error('No se pudieron cargar las promociones')))
            .then(data => {
                if (!Array.isArray(data)) return;
                const definitions = data.filter(definition =>
                    definition?.producto && Array.isArray(definition?.items) && definition.items.length > 0
                ) as AutomaticPromotionDefinition[];
                setAutomaticPromotions(definitions);
                setCart(current => applyAutomaticPromotions(current, definitions).cart);
            })
            .catch(error => {
                if (!controller.signal.aborted) console.error('Error cargando promociones automaticas', error);
            });

        return () => controller.abort();
    }, []);

    const addToCart = (product: Product, quantity = 1) => {
        if (isProductOutOfStock(product)) {
            return;
        }
        const requestedQuantity = Math.max(0, Math.floor(Number(quantity) || 0));
        if (!requestedQuantity) return;
        const cartKey = product.cartKey || createCartKey(product);
        setCart(currentCart => {
            const existingItem = currentCart.find(item => item.cartKey === cartKey);
            const stockLimit = getProductStockLimit(product);
            const currentProductQuantity = currentCart
                .filter(item => item.idProductos === product.idProductos)
                .reduce((total, item) => total + item.quantity, 0);
            const quantityToAdd = stockLimit === null
                ? requestedQuantity
                : Math.min(requestedQuantity, Math.max(0, stockLimit - currentProductQuantity));
            if (!quantityToAdd) return currentCart;
            let nextCart: CartItem[];
            if (existingItem) {
                nextCart = currentCart.map(item =>
                    item.cartKey === cartKey
                        ? { ...item, quantity: item.quantity + quantityToAdd }
                        : item
                );
            } else {
                nextCart = [...currentCart, { ...product, cartKey, quantity: quantityToAdd }];
            }
            return applyAutomaticPromotions(nextCart, automaticPromotions).cart;
        });
    };

    const decreaseQuantity = (itemKey: number | string) => {
        setCart(currentCart => {
            const existingItem = currentCart.find(item => matchesItemKey(item, itemKey));
            if (existingItem?.quantity === 1) {
                return currentCart.filter(item => !matchesItemKey(item, itemKey));
            }
            return currentCart.map(item =>
                matchesItemKey(item, itemKey)
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            );
        });
    };

    const removeFromCart = (itemKey: number | string) => {
        setCart(currentCart => currentCart.filter(item => !matchesItemKey(item, itemKey)));
    };

    const clearCart = () => {
        setCart([]);
    };

    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, decreaseQuantity, clearCart, totalItems }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
